import { LayoutEnum } from 'src/layouts/constants';
import { permissionKeys, routePaths } from 'utils/constants';
import ActivityLogsPage from 'src/pages/admin/activity-logs';
import { routeType } from 'utils/proptypes';
import BookingFailureTracker from 'src/pages/admin/booking-failure-logs';
import BookingFailureDetails from 'src/pages/admin/booking-failure-logs/BookingFailureDetails';
import SiteTouristCenterOverrides from 'src/pages/admin/site-config-registry/SiteTouristCenterOverrides';
import {
   FullPageRedirect,
   AccountDisabledPage,
   BookingPage,
   HotelDetail,
   HotelGallery,
   HotelMapView,
   HotelSearch,
   LandingPage,
   LoginPage,
   LogoutPage,
   NotFoundPage,
   OrganizationLandingPage,
   EventLandingPage,
   RegisterPage,
   SignupPage,
   UnauthorizedPage,
   MyAccountPage,
   MyFavoritesPage,
   OrganizationPage,
   AddEditOrganizationPage,
   ManageTripsPage,
   TripDetailPage,
   DisputesPage,
   FraudWatchPage,
   FraudUserDetailPage,
   ProgramSchedulePage,
   ProgramScheduleDetailPage,
   GameGuideEditorPage,
   TeamHotelsPage,
   GameGuidePage,
   PlanSeasonPage,
   RolePage,
   TransactionPage,
   ProceedsReportPage,
   UserPage,
   CouponPage,
   ReviewPage,
   HotelBlacklistPage,
   Settings,
   SiteConfigRegistry,
   FontsPage,
} from 'src/router/lazyPages';

const publicRoutes: routeType[] = [
   { path: routePaths.ACCOUNT_DISABLED, component: AccountDisabledPage, layout: LayoutEnum.USER, title: 'Booking' },
   { path: routePaths.BOOKING, component: BookingPage, layout: LayoutEnum.BOOKING, title: 'Booking' },
   { path: routePaths.HOTEL_DETAIL, component: HotelDetail, layout: LayoutEnum.HOTEL, title: 'Hotel Detail', titleSource: 'seo' },
   { path: routePaths.HOTEL_GALLERY, component: HotelGallery, title: 'Hotel Gallery', titleSource: 'ssr' },
   { path: routePaths.HOTEL_MAP_VIEW, component: HotelMapView, layout: LayoutEnum.MAP, title: 'Hotel map view result', titleSource: 'ssr' },
   { path: routePaths.HOTEL_SEARCH, component: HotelSearch, layout: LayoutEnum.HOTEL, title: 'Hotel Search Results', titleSource: 'seo' },
   { path: routePaths.LANDING, component: LandingPage, title: 'Welcome', titleSource: 'seo' },
   { path: routePaths.LOGIN_PAGE, component: LoginPage, title: 'Login' },
   { path: routePaths.LOGOUT_PAGE, component: LogoutPage, title: 'Logout' },
   { path: routePaths.NOT_FOUND_PAGE, component: NotFoundPage, layout: LayoutEnum.USER, title: 'Page Not Found' },
   {
      path: routePaths.ORGANIZATION.DETAILS,
      component: OrganizationLandingPage,
      layout: LayoutEnum.ORGANIZATION,
      title: 'Organization Detail',
      titleSource: 'seo',
   },
   {
      path: routePaths.ORGANIZATION.LANDING,
      component: () => <FullPageRedirect to={routePaths.LANDING} />,
      title: 'Redirect',
   },
   { path: routePaths.EVENT.LANDING, component: EventLandingPage, layout: LayoutEnum.ORGANIZATION, title: 'Event Detail', titleSource: 'seo' },
   { path: routePaths.GAME_GUIDE, component: GameGuidePage, layout: LayoutEnum.ORGANIZATION, title: 'Game Guide', titleSource: 'ssr' },
   { path: routePaths.PLAN_SEASON, component: PlanSeasonPage, layout: LayoutEnum.ORGANIZATION, title: 'Plan Your Season', titleSource: 'ssr' },
   { path: routePaths.PLAN_SEASON_PROGRAM, component: PlanSeasonPage, layout: LayoutEnum.ORGANIZATION, title: 'Plan Your Season', titleSource: 'ssr' },
   { path: routePaths.REGISTER_PAGE, component: RegisterPage, title: 'Register' },
   { path: routePaths.SIGNUP, component: SignupPage, title: 'Sign Up' },
   { path: routePaths.UNAUTHORIZED, component: UnauthorizedPage, layout: LayoutEnum.USER, title: 'Unauthorized' },
];

const protectedRoutes: routeType[] = [
   { path: routePaths.MY_ACCOUNT, component: MyAccountPage, layout: LayoutEnum.USER, title: 'My account', group: 'account' },
   { path: routePaths.MY_FAVORITES, component: MyFavoritesPage, layout: LayoutEnum.USER, title: 'My Lists' },
   { path: routePaths.MY_PRICE_TRACKING, component: MyAccountPage, layout: LayoutEnum.USER, title: 'Price Tracking', group: 'account' },
   { path: routePaths.MY_TRIPS, component: MyAccountPage, layout: LayoutEnum.USER, title: 'My trips', group: 'account' },
   { path: routePaths.MY_TRIP_DETAIL, component: MyAccountPage, layout: LayoutEnum.USER, title: 'My trip detail', group: 'account' },
   { path: routePaths.PAYMENT_METHODS, component: MyAccountPage, layout: LayoutEnum.USER, title: 'Payment methods', group: 'account' },
   {
      path: routePaths.ADMIN.ORGANIZATION.INDEX,
      component: OrganizationPage,
      layout: LayoutEnum.ADMIN,
      title: 'Organization management',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.ORGANIZATION.ADD,
      component: AddEditOrganizationPage,
      layout: LayoutEnum.ADMIN,
      title: 'Add a new organization',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.ORGANIZATION.EDIT,
      component: AddEditOrganizationPage,
      layout: LayoutEnum.ADMIN,
      title: 'Edit an organization',
      permissions: [permissionKeys.WRITE_ORG, permissionKeys.READ_ORG],
   },
   {
      path: routePaths.ADMIN.MANAGE_TRIP.INDEX,
      component: ManageTripsPage,
      layout: LayoutEnum.ADMIN,
      title: 'Trips management',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   // NPT-2087 — the admin create-booking wizard route is unregistered in 2.24.0 (see manage-trips/index.tsx).
   {
      path: routePaths.ADMIN.MANAGE_TRIP.DETAIL,
      component: TripDetailPage,
      layout: LayoutEnum.ADMIN,
      title: 'Trips management',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.TEAM_HOTELS,
      component: TeamHotelsPage,
      layout: LayoutEnum.ADMIN,
      title: 'Team hotels',
      permissions: [permissionKeys.WRITE_SCHEDULE],
   },
   {
      path: routePaths.ADMIN.PROGRAM_SCHEDULE.INDEX,
      component: ProgramSchedulePage,
      layout: LayoutEnum.ADMIN,
      title: 'Program schedules',
      permissions: [permissionKeys.WRITE_SCHEDULE, permissionKeys.READ_SCHEDULE],
   },
   {
      path: routePaths.ADMIN.PROGRAM_SCHEDULE.DETAIL,
      component: ProgramScheduleDetailPage,
      layout: LayoutEnum.ADMIN,
      title: 'Program schedules',
      permissions: [permissionKeys.WRITE_SCHEDULE, permissionKeys.READ_SCHEDULE],
   },
   {
      path: routePaths.ADMIN.PROGRAM_SCHEDULE.GAME_GUIDE,
      component: GameGuideEditorPage,
      layout: LayoutEnum.ADMIN,
      title: 'Game guide',
      permissions: [permissionKeys.WRITE_SCHEDULE, permissionKeys.READ_SCHEDULE],
   },
   {
      path: routePaths.ADMIN.USER.INDEX,
      component: UserPage,
      layout: LayoutEnum.ADMIN,
      title: 'User management',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.ROLE.INDEX,
      component: RolePage,
      layout: LayoutEnum.ADMIN,
      title: 'Role management',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.TRANSACTIONS.INDEX,
      component: TransactionPage,
      layout: LayoutEnum.ADMIN,
      title: 'Transaction management',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.PROCEEDS_REPORT.INDEX,
      component: ProceedsReportPage,
      layout: LayoutEnum.ADMIN,
      title: 'Proceeds report',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.COUPON.INDEX,
      component: CouponPage,
      layout: LayoutEnum.ADMIN,
      title: 'Coupon management',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.REVIEW.INDEX,
      component: ReviewPage,
      layout: LayoutEnum.ADMIN,
      title: 'Review management',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.HOTEL_BLACKLIST.INDEX,
      component: HotelBlacklistPage,
      layout: LayoutEnum.ADMIN,
      title: 'Hotel blacklist management',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.SITE_CONFIG_REGISTRY,
      component: SiteConfigRegistry,
      layout: LayoutEnum.ADMIN,
      title: 'Site config registry',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.FONTS,
      component: FontsPage,
      layout: LayoutEnum.ADMIN,
      title: 'Fonts',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.ACTIVITY_LOGS,
      component: ActivityLogsPage,
      layout: LayoutEnum.ADMIN,
      title: 'Activity logs',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.DISPUTES,
      component: DisputesPage,
      layout: LayoutEnum.ADMIN,
      title: 'Disputes',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.BOOKING_FAILURE_TRACKER.INDEX,
      component: BookingFailureTracker,
      layout: LayoutEnum.ADMIN,
      title: 'Booking failure tracker',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.BOOKING_FAILURE_TRACKER.DETAIL,
      component: BookingFailureDetails,
      layout: LayoutEnum.ADMIN,
      title: 'Booking failure detail',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.FRAUD_WATCH.INDEX,
      component: FraudWatchPage,
      layout: LayoutEnum.ADMIN,
      title: 'Fraud watch',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.FRAUD_WATCH.DETAIL,
      component: FraudUserDetailPage,
      layout: LayoutEnum.ADMIN,
      title: 'Fraud watch user',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
   {
      path: routePaths.ADMIN.TOURIST_OVERRIDE,
      component: SiteTouristCenterOverrides,
      layout: LayoutEnum.ADMIN,
      title: 'Tourist override',
      permissions: [permissionKeys.PLATFORM_ADMIN],
   },
];

const routes: routeType[] = [...publicRoutes, ...protectedRoutes];

export { protectedRoutes, publicRoutes };
export default routes;
